import React from 'react';
import { Link } from 'react-router-dom';
import {
  Landmark,
  Wallet,
  BadgePercent,
  Sun,
  BatteryCharging,
  Droplets,
  Snowflake,
  CookingPot,
  Layers,
  Wind,
  PanelsTopLeft,
  Home,
  Users,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  ArrowDown,
  Sparkles,
  ClipboardCheck,
  ExternalLink,
  PiggyBank,
} from 'lucide-react';
import { Header } from '../components/Header';
import { Seo, SITE_URL, SITE_NAME } from '../components/Seo';
import { LOGO_URL } from '../data/mock';
import { LEGAL_ENTITY } from '../components/Footer';
import { ExpertBadge } from '../components/ExpertBadge';

const SOURCE_URL = 'https://www.energy.nsw.gov.au/households/grants-rebates/home-energy-saver';

const COVERED = [
  { icon: Sun, label: 'Rooftop solar' },
  { icon: BatteryCharging, label: 'Home batteries' },
  { icon: Droplets, label: 'Heat pump hot water' },
  { icon: Sun, label: 'Solar hot water' },
  { icon: Snowflake, label: 'Reverse-cycle air con' },
  { icon: CookingPot, label: 'Induction cooktops' },
  { icon: Layers, label: 'Ceiling insulation' },
  { icon: Wind, label: 'Draught-proofing' },
  { icon: PanelsTopLeft, label: 'Double glazing' },
  { icon: ClipboardCheck, label: 'Switchboard upgrades' },
];

const STEPS = [
  { title: 'Check your eligibility', body: 'Confirm whether you qualify as a homeowner or renter — it only takes a moment.' },
  { title: 'Choose your upgrades', body: 'Solar, battery, hot water, air con and more are covered. We help you compare what suits your home.' },
  { title: 'Get your quotes', body: 'We connect you with trusted local providers for clear, no-obligation quotes.' },
  { title: 'Apply for your loan and/or discount', body: 'Choose a finance provider and lock in your interest-free loan and eligible discount.' },
];

const FAQS = [
  {
    q: 'What is the NSW Home Energy Saver program?',
    a: 'It is a NSW Government program that lowers the upfront cost of energy-saving home upgrades. Eligible households can access a discount of up to $4,000 and/or a zero-interest loan of up to $15,000 (repayable over 10 years) towards upgrades like solar, batteries, hot water and more.',
  },
  {
    q: 'Who is eligible?',
    a: 'Homeowners who own the property and are an Australian citizen or permanent resident can access the interest-free loan with a combined household income up to $210,000. The up to $4,000 discount is for lower-income households (combined income up to $80,000 or an eligible concession card) and is available to both homeowners and renters named on the lease.',
  },
  {
    q: 'Which upgrades are covered?',
    a: 'Rooftop solar, home batteries, switchboard upgrades, heat pump and solar hot water, reverse-cycle air conditioners, induction cooktops, ceiling insulation, draught-proofing, double glazing and NatHERS assessments.',
  },
  {
    q: 'How do I get started?',
    a: 'Start by comparing your options with us — it is free and takes about two minutes. We will help you understand what you may be eligible for and connect you with trusted providers for quotes.',
  },
];

const NswHomeEnergySaverPage = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="nsw-home-energy-saver-page">
      <Seo
        title={`NSW Home Energy Saver: Up to $4,000 Off + $15,000 Interest-Free | ${SITE_NAME}`}
        description="The NSW Home Energy Saver offers eligible households up to $4,000 off and a $15,000 interest-free loan for solar, batteries, hot water and more. Check your eligibility and get a free quote."
        path="/nsw-home-energy-saver"
        jsonLd={jsonLd}
      />
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gray-950 text-white" data-testid="nsw-hero">
        <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 bg-emerald-500/20 rounded-full blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-24 w-96 h-96 bg-cyo-orange/15 rounded-full blur-3xl" />

        <div className="relative max-w-4xl mx-auto px-4 pt-12 pb-14 sm:pt-16 sm:pb-20 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 px-4 py-1.5 rounded-full">
            <Landmark className="w-4 h-4" />
            <span className="text-xs sm:text-sm font-bold tracking-wide">NSW Government — major new announcement</span>
          </div>

          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
            The NSW <span className="text-cyo-orange">Home Energy Saver</span> is here
          </h1>

          <p className="mt-6 text-base sm:text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Eligible NSW households can now slash the upfront cost of solar, batteries, hot water
            and more — with a discount of <span className="font-bold text-white">up to $4,000</span>{' '}
            plus an <span className="font-bold text-white">interest-free loan of up to $15,000</span>.
          </p>

          {/* Big offer chips */}
          <div className="mt-9 grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="nsw-hero-discount">
              <BadgePercent className="w-7 h-7 text-emerald-300 mx-auto" />
              <p className="mt-2 text-3xl font-black tabular-nums">up to $4,000</p>
              <p className="text-sm text-gray-300">discount on eligible upgrades</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="nsw-hero-loan">
              <Wallet className="w-7 h-7 text-cyo-orange mx-auto" />
              <p className="mt-2 text-3xl font-black tabular-nums">up to $15,000</p>
              <p className="text-sm text-gray-300">interest-free loan over 10 years</p>
            </div>
          </div>

          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/compare"
              data-testid="nsw-hero-cta"
              className="inline-flex items-center justify-center gap-2 bg-cyo-orange hover:bg-orange-600 text-white font-bold px-7 py-4 rounded-full shadow-lg shadow-orange-900/30 hover:-translate-y-0.5 transition-all"
            >
              Check My Eligibility
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#nsw-offers"
              data-testid="nsw-hero-secondary"
              className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur border border-white/25 text-white font-bold px-7 py-4 rounded-full hover:bg-white/20 transition-all"
            >
              How it works
              <ArrowDown className="w-4 h-4" />
            </a>
          </div>
          <p className="mt-5 text-xs text-gray-400 flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" />
            100% free, no-obligation eligibility check
          </p>
          <div className="mt-6 flex justify-center">
            <ExpertBadge variant="dark" />
          </div>
        </div>
      </section>

      {/* THE TWO OFFERS */}
      <section id="nsw-offers" className="max-w-5xl mx-auto px-4 py-14 sm:py-20 scroll-mt-16" data-testid="nsw-offers">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" />
            Two ways to save
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">
            What the program offers
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
          <div className="rounded-3xl border-2 border-emerald-200 bg-emerald-50/40 p-6 sm:p-8" data-testid="nsw-offer-discount">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center mb-4">
              <BadgePercent className="w-6 h-6" />
            </div>
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">Discount</p>
            <p className="mt-2 text-4xl font-black tabular-nums text-gray-900">up to $4,000</p>
            <p className="mt-3 text-sm text-gray-600 leading-relaxed">
              For lower-income households — combined income up to <strong>$80,000</strong> or an
              eligible concession card. Available to homeowners <em>and</em> renters.
            </p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-100 px-3 py-1 rounded-full">
              Applications opening soon — register your interest
            </span>
          </div>

          <div className="rounded-3xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm" data-testid="nsw-offer-loan">
            <div className="w-12 h-12 rounded-xl bg-orange-50 text-cyo-orange flex items-center justify-center mb-4">
              <Wallet className="w-6 h-6" />
            </div>
            <p className="text-xs font-bold uppercase tracking-widest text-cyo-orange">Interest-free loan</p>
            <p className="mt-2 text-4xl font-black tabular-nums text-gray-900">up to $15,000</p>
            <p className="mt-3 text-sm text-gray-600 leading-relaxed">
              Zero-interest, repayable over <strong>10 years</strong>. For households with a
              combined income up to <strong>$210,000</strong>.
            </p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-1 rounded-full">
              <CheckCircle2 className="w-3.5 h-3.5" /> Open now
            </span>
          </div>
        </div>
        <p className="mt-5 text-center text-sm text-gray-600 max-w-2xl mx-auto">
          Eligible households may be able to combine both — a discount <em>and</em> a loan — to make
          quality upgrades genuinely affordable.
        </p>
        <div className="mt-6 text-center">
          <a
            href={SOURCE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nsw-official-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:text-emerald-800 underline underline-offset-2"
          >
            Read the official NSW Government program details
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </section>

      {/* WHO IS ELIGIBLE */}
      <section className="relative bg-gradient-to-b from-emerald-50/60 via-white to-orange-50/30 py-14 sm:py-20" data-testid="nsw-eligibility">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-1.5 bg-orange-50 border border-orange-200 text-cyo-orange px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
              <Users className="w-3.5 h-3.5" />
              Who is eligible
            </div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">
              Could you qualify?
            </h2>
            <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
              Support is available for both homeowners and renters across NSW.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-2xl p-6" data-testid="nsw-eligible-homeowners">
              <div className="w-12 h-12 rounded-xl bg-orange-50 text-cyo-orange flex items-center justify-center mb-4">
                <Home className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Homeowners</h3>
              <ul className="mt-3 space-y-2">
                {['You own the property', 'Australian citizen or permanent resident', 'Combined household income up to $210,000 (for the interest-free loan)', 'Income up to $80,000 or a concession card for the discount'].map((t) => (
                  <li key={t} className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-6" data-testid="nsw-eligible-renters">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-4">
                <Users className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Renters</h3>
              <ul className="mt-3 space-y-2">
                {['You are named on the lease', 'Combined household income up to $80,000, or', 'You hold an eligible concession card', 'You can apply again if you move home'].map((t) => (
                  <li key={t} className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="mt-5 text-center text-[12px] text-gray-500 max-w-2xl mx-auto">
            Eligible concession cards include the Health Care Card, Low Income Health Care Card,
            Pensioner Concession Card and Veteran Gold Card. Final eligibility is determined by the
            NSW Government program.
          </p>
        </div>
      </section>

      {/* WHAT'S COVERED */}
      <section className="max-w-5xl mx-auto px-4 py-14 sm:py-20" data-testid="nsw-covered">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">What you can upgrade</h2>
          <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
            The program covers a wide range of energy-saving home upgrades.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          {COVERED.map(({ icon: Icon, label }, i) => (
            <div
              key={`${label}-${i}`}
              data-testid={`nsw-covered-${i}`}
              className="bg-white border border-gray-200 rounded-2xl p-4 text-center hover:shadow-lg hover:border-cyo-orange/30 transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-gray-50 text-cyo-orange flex items-center justify-center mx-auto mb-2">
                <Icon className="w-5 h-5" />
              </div>
              <p className="text-sm font-semibold text-gray-800 leading-snug">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW TO APPLY */}
      <section className="relative bg-gray-950 text-white py-14 sm:py-20" data-testid="nsw-apply">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight">How to apply</h2>
            <p className="mt-3 text-gray-400 max-w-2xl mx-auto text-sm sm:text-base">
              We make it simple — start with a free comparison and we'll guide you through.
            </p>
          </div>
          <div className="space-y-4">
            {STEPS.map((s, i) => (
              <div key={s.title} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-5">
                <div className="w-10 h-10 rounded-full bg-cyo-orange text-white flex items-center justify-center font-black flex-shrink-0">
                  {i + 1}
                </div>
                <div>
                  <h3 className="text-base font-bold">{s.title}</h3>
                  <p className="mt-1 text-sm text-gray-400 leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-xs text-gray-500 max-w-2xl mx-auto">
            A minimum household contribution of $200 applies, and only one discount is available per
            household. Renters can apply again if they move.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 py-14 sm:py-20" data-testid="nsw-faq">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">Frequently asked questions</h2>
        </div>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <div key={i} className="bg-gray-50 border border-gray-100 rounded-2xl p-5" data-testid={`nsw-faq-${i}`}>
              <h3 className="font-bold text-gray-900">{f.q}</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative bg-gradient-to-br from-cyo-orange to-orange-600 text-white py-14 sm:py-20 overflow-hidden" data-testid="nsw-final-cta">
        <div className="pointer-events-none absolute inset-0 opacity-30">
          <div className="absolute -top-20 -left-20 w-80 h-80 bg-white/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -right-10 w-96 h-96 bg-yellow-200/30 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <PiggyBank className="w-10 h-10 mx-auto mb-4 opacity-90" />
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
            Don't leave money on the table
          </h2>
          <p className="mt-4 text-white/90 max-w-xl mx-auto text-sm sm:text-base">
            The NSW Home Energy Saver could make solar, a battery or a heat pump far more affordable.
            Check your eligibility and get a free, no-obligation quote today.
          </p>
          <div className="mt-7">
            <Link
              to="/compare"
              data-testid="nsw-final-cta-btn"
              className="inline-flex items-center justify-center gap-2 bg-white text-cyo-orange font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              Check My Eligibility &amp; Get a Quote
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-950 text-gray-400 py-10">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <Link to="/" className="inline-block">
            <img src={LOGO_URL} alt={LEGAL_ENTITY.website} className="h-8 w-auto mx-auto brightness-0 invert opacity-90" />
          </Link>
          <p className="mt-4 text-xs sm:text-sm">
            {LEGAL_ENTITY.website} is owned and operated by {LEGAL_ENTITY.name} (ABN {LEGAL_ENTITY.abn}).
          </p>
          <p className="mt-2 text-[11px] leading-relaxed max-w-2xl mx-auto text-gray-500">
            The Home Energy Saver is a NSW Government program. Eligibility, offer amounts, covered
            products, dates and conditions are set by the NSW Government and may change.
            CompareYourOptions.com.au is a free comparison service that connects interested
            households with trusted providers and does not administer the program or supply or
            install systems. This page is a general summary and not financial or eligibility advice.
          </p>
          <a
            href={SOURCE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nsw-source-link"
            className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-white transition-colors"
          >
            Source: NSW Government — Home Energy Saver
            <ExternalLink className="w-3 h-3" />
          </a>
          <div className="mt-4 flex justify-center gap-4 text-xs">
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <span>•</span>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <span>•</span>
            <Link to="/about" className="hover:text-white">About</Link>
          </div>
          <p className="mt-6 text-[11px] text-gray-600">
            © {new Date().getFullYear()} {LEGAL_ENTITY.website}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default NswHomeEnergySaverPage;
